import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";

import bundledContent from "./edge_mentor_training_content.json";
import edgeMentorContentContract from "./edgeMentorContentContract.json";
import { resolveEdgeMentorVideoSource } from "./edgeMentorVideoSource";

import {
  edgeMentorDiagnostic,
  edgeMentorScanContent,
} from "./edgeMentorDiagnostics";
import {
  getActiveEdgeMentorModule,
} from "./edgeMentorModuleRegistry";


const MESSAGE_DURATION_MS = 15000;

const {
  schema: EDGE_MENTOR_SCHEMA,
  schema_version: EDGE_MENTOR_SCHEMA_VERSION,
  content_version: EDGE_MENTOR_CONTENT_VERSION,
} = edgeMentorContentContract;

const validateEdgeMentorContentDocument = (content) => {
  if (!content || typeof content !== "object") {
    return "content document is missing or invalid";
  }

  if (content.schema !== EDGE_MENTOR_SCHEMA) {
    return `unsupported schema "${String(content.schema ?? "missing")}"`;
  }

  if (Number(content.schema_version) !== EDGE_MENTOR_SCHEMA_VERSION) {
    return `unsupported schema version "${String(content.schema_version ?? "missing")}"`;
  }

  if (Number(content.content_version) !== EDGE_MENTOR_CONTENT_VERSION) {
    return `unsupported content version "${String(content.content_version ?? "missing")}"`;
  }

  if (!Array.isArray(content.entries)) {
    return "content entries are missing or invalid";
  }

  return "";
};

const normalizeModuleKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

// Current workbook/JSON is Pressure Vessel-only and intentionally has no
// module field yet. Keep those legacy entries available only to PV so another
// module can never resolve PV content just because it reuses the same code_label.
const LEGACY_UNSCOPED_MODULE_KEY = "pv";

const getEntryModuleKey = (item) =>
  normalizeModuleKey(
    item?.module_key ??
      item?.moduleKey ??
      item?.module ??
      "",
  );

const getContentLookupKey = (moduleKey, codeLabel) =>
  `${normalizeModuleKey(moduleKey)}::${String(codeLabel ?? "").trim()}`;

let contentLookupCache = null;

const buildContentLookup = (entries) => {
  const lookup = new Map();

  (Array.isArray(entries) ? entries : []).forEach((item) => {
    const codeLabel = String(item?.code_label ?? "").trim();

    if (!codeLabel) {
      return;
    }

    const entryModuleKey =
      getEntryModuleKey(item) || LEGACY_UNSCOPED_MODULE_KEY;
    const lookupKey = getContentLookupKey(
      entryModuleKey,
      codeLabel,
    );

    // Preserve Array.find() behavior if duplicate identities ever reach runtime:
    // the first matching entry wins.
    if (!lookup.has(lookupKey)) {
      lookup.set(lookupKey, item);
    }
  });

  return lookup;
};

const updateContentLookupCache = (entries, source, force = false) => {
  if (
    force ||
    !contentLookupCache ||
    contentLookupCache.source !== source
  ) {
    contentLookupCache = {
      source,
      lookup: buildContentLookup(entries),
    };
  }
};

const getBridge = () =>
  typeof window !== "undefined"
    ? window.electron?.edgeMentor
    : null;

async function loadLatestContent() {
  const bridge = getBridge();

  if (typeof bridge?.refreshContent === "function") {
    const result = await bridge.refreshContent();

    if (result?.content) {
      const runtimeValidationError =
        validateEdgeMentorContentDocument(result.content);

      if (!runtimeValidationError) {
        if (!result.ok && result.error) {
          console.warn(
            "[Edge Mentor] Content refresh failed; using last valid JSON:",
            result.error,
          );
        }

        edgeMentorScanContent(result.content.entries);
        updateContentLookupCache(
          result.content.entries,
          "runtime",
          Boolean(result.refreshed),
        );
        return result.content;
      }

      console.warn(
        "[Edge Mentor] Runtime content rejected; using bundled content:",
        runtimeValidationError,
      );
    } else if (result?.error) {
      throw new Error(result.error);
    }
  }

  const bundledValidationError =
    validateEdgeMentorContentDocument(bundledContent);

  if (bundledValidationError) {
    throw new Error(
      `[Edge Mentor] Bundled content rejected: ${bundledValidationError}`,
    );
  }

  edgeMentorScanContent(bundledContent.entries);
  updateContentLookupCache(
    bundledContent.entries,
    "bundled",
  );
  return bundledContent;
}

export function useEdgeMentor(moduleKey = LEGACY_UNSCOPED_MODULE_KEY) {
  const [entry, setEntry] = useState(null);
  const [messageVersion, setMessageVersion] = useState(0);
  const requestIdRef = useRef(0);

  useEffect(() => {
    let active = true;

    loadLatestContent().catch((error) => {
      if (active) {
        console.warn(
          "[Edge Mentor] Startup refresh failed:",
          error,
        );
      }
    });

    return () => {
      active = false;
    };
  }, []);

  const closeMessage = useCallback(() => {
    requestIdRef.current += 1;
    setEntry(null);
  }, []);

  const showForCodeLabel = useCallback(
    async (codeLabel, diagnosticSource = "") => {
    const normalizedCodeLabel = String(
      codeLabel ?? "",
    ).trim();

    const activeModuleKey = normalizeModuleKey(
      getActiveEdgeMentorModule()?.moduleKey ||
        moduleKey ||
        LEGACY_UNSCOPED_MODULE_KEY,
    );

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!normalizedCodeLabel) {
      setEntry(null);
      return;
    }

    try {
      const content = await loadLatestContent();

      if (requestIdRef.current !== requestId) {
        return;
      }

      const lookupKey = getContentLookupKey(
        activeModuleKey,
        normalizedCodeLabel,
      );
      const match =
        contentLookupCache?.lookup.get(lookupKey) || null;

      if (!match) {
        edgeMentorDiagnostic("missing-content", {
          a: diagnosticSource || normalizedCodeLabel,
          b: normalizedCodeLabel,
          n: Array.isArray(content?.entries)
            ? content.entries.length
            : 0,
        });
      }

      setEntry(match || null);

      if (match) {
        setMessageVersion((value) => value + 1);
      }
    } catch (error) {
      if (requestIdRef.current !== requestId) {
        return;
      }

      console.warn(
        `[Edge Mentor] Could not load content for ${normalizedCodeLabel}:`,
        error,
      );

      setEntry(null);
    }
  }, [moduleKey]);

  return {
    entry,
    messageVersion,
    showForCodeLabel,
    closeMessage,
  };
}

function uidTypeLabel(uidType) {
  const normalized = String(
    uidType ?? "",
  )
    .trim()
    .toLowerCase();

  if (normalized === "primary") {
    return "Primary UID";
  }

  if (normalized === "secondary") {
    return "Secondary UID";
  }

  if (normalized === "derived") {
    return "Derived UID";
  }

  return normalized ? `${normalized} UID` : "";
}

const fullTextSx = {
  display: "block",
  whiteSpace: "normal",
  overflow: "visible",
  textOverflow: "clip",
  wordBreak: "normal",
  overflowWrap: "break-word",
  WebkitLineClamp: "unset",
  WebkitBoxOrient: "initial",
  maxWidth: "none",
};

function getMessageVisual(entry, theme, isTabMessage) {
  const messageType = String(
    entry?.severity ||
      entry?.message_severity ||
      entry?.message_type ||
      "",
  )
    .trim()
    .toLowerCase();

  if (
    messageType === "error" ||
    messageType === "critical"
  ) {
    return {
      accent: theme.palette.error.main,
      Icon: ErrorOutlineRoundedIcon,
      symbolKind: null,
      label: "Error",
    };
  }

  if (
    messageType === "warning" ||
    messageType === "warn"
  ) {
    return {
      accent: theme.palette.warning.main,
      Icon: WarningAmberRoundedIcon,
      symbolKind: null,
      label: "Warning",
    };
  }

  if (
    messageType === "success" ||
    messageType === "complete"
  ) {
    return {
      accent: theme.palette.success.main,
      Icon: CheckCircleOutlineRoundedIcon,
      symbolKind: null,
      label: "Success",
    };
  }

  if (isTabMessage) {
    return {
      accent: "#06B6D4",
      Icon: SettingsOutlinedIcon,
      symbolKind: null,
      label: "How it works",
    };
  }

  return {
    accent:
      theme.palette.info?.main ||
      theme.palette.primary.main,
    Icon: null,
    symbolKind: "info",
    label: "Information",
  };
}

function MentorSymbolGlyph({ kind }) {
  if (kind === "tutorial") {
    return (
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect x="12" y="18" width="40" height="34" rx="6" stroke="currentColor" strokeWidth="3.4" />
        <path d="M12 24H52" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" />
        <path d="M16 12H48C50.7 12 52 13.5 52 16V24H12V16C12 13.5 13.3 12 16 12Z" stroke="currentColor" strokeWidth="3.4" strokeLinejoin="round" />
        <path d="M20 12L29 24" stroke="currentColor" strokeWidth="3.1" strokeLinecap="round" />
        <path d="M31 12L40 24" stroke="currentColor" strokeWidth="3.1" strokeLinecap="round" />
        <path d="M42 12L48 20" stroke="currentColor" strokeWidth="3.1" strokeLinecap="round" />
        <path d="M30 31L40 36.8L30 42.6V31Z" fill="currentColor" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      </svg>
    );
  }

  if (kind === "howItWorks") {
    return (
      <svg
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g
          stroke="currentColor"
          strokeWidth="3.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="32" cy="32" r="13" />
          <circle cx="32" cy="32" r="5.5" />

          <path d="M32 10V16" />
          <path d="M32 48V54" />
          <path d="M10 32H16" />
          <path d="M48 32H54" />

          <path d="M16.5 16.5L20.8 20.8" />
          <path d="M43.2 43.2L47.5 47.5" />
          <path d="M16.5 47.5L20.8 43.2" />
          <path d="M43.2 20.8L47.5 16.5" />
        </g>
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="32" cy="32" r="24" stroke="currentColor" strokeWidth="3.2" />
      <circle cx="32" cy="20" r="3.4" fill="currentColor" />
      <path d="M32 28V45" stroke="currentColor" strokeWidth="4.6" strokeLinecap="round" />
      <path d="M27.6 45H36.4" stroke="currentColor" strokeWidth="4.6" strokeLinecap="round" />
    </svg>
  );
}

function SymbolArt({
  kind = "info",
  alt,
  size = 26,
  frame = false,
  accent = "#06B6D4",
  frameSize,
}) {
  const outerSize = frameSize || size + 18;

  return (
    <Box
      aria-label={alt}
      sx={{
        width: outerSize,
        height: outerSize,
        borderRadius: 1.75,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: accent,
        backgroundColor: frame
          ? alpha(accent, 0.10)
          : "transparent",
        border: frame
          ? `1px solid ${alpha(accent, 0.18)}`
          : "none",
        boxShadow: frame
          ? `0 4px 14px ${alpha(accent, 0.10)}`
          : "none",
        '& svg': {
          display: 'block',
          width: size,
          height: size,
        },
      }}
    >
      <MentorSymbolGlyph kind={kind} />
    </Box>
  );
}

export default function EdgeMentorMessage({
  entry,
  messageVersion = 0,
  onClose,
}) {
  const theme = useTheme();

  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const remainingMsRef = useRef(
    MESSAGE_DURATION_MS,
  );
  const hoveredRef = useRef(false);
  const focusedRef = useRef(false);
  const videoOpenRef = useRef(false);
  const activeEntryKeyRef = useRef("");

  const [videoOpen, setVideoOpen] =
    useState(false);

  const [videoState, setVideoState] =
    useState("idle");

  const [videoErrorMessage, setVideoErrorMessage] =
    useState("");

  const [videoRetryVersion, setVideoRetryVersion] =
    useState(0);

  const [messageRightOffset, setMessageRightOffset] =
    useState(28);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const pauseTimer = useCallback(() => {
    if (!timerRef.current) {
      return;
    }

    const elapsed =
      Date.now() - startedAtRef.current;

    remainingMsRef.current = Math.max(
      0,
      remainingMsRef.current - elapsed,
    );

    clearTimer();
  }, [clearTimer]);

  const startTimer = useCallback(() => {
    clearTimer();

    if (
      !entry ||
      hoveredRef.current ||
      focusedRef.current ||
      videoOpenRef.current
    ) {
      return;
    }

    const delay = Math.max(
      0,
      remainingMsRef.current,
    );

    startedAtRef.current = Date.now();

    timerRef.current = setTimeout(() => {
      timerRef.current = null;

      // If the video preview opened (or the card is hovered/focused) at the
      // moment this timeout fired, do not close the Edge Mentor message.
      if (
        videoOpenRef.current ||
        hoveredRef.current ||
        focusedRef.current
      ) {
        return;
      }

      remainingMsRef.current = 0;
      onClose?.();
    }, delay);
  }, [clearTimer, entry, onClose]);

  useEffect(() => {
    const nextEntryKey = String(
      entry?.code_label ||
        entry?.field_label ||
        "",
    ).trim();

    const previousEntryKey =
      activeEntryKeyRef.current;

    const sameLogicalEntry =
      Boolean(nextEntryKey) &&
      nextEntryKey === previousEntryKey;

    activeEntryKeyRef.current =
      nextEntryKey;

    clearTimer();
    hoveredRef.current = false;
    focusedRef.current = false;
    remainingMsRef.current =
      MESSAGE_DURATION_MS;

    if (!entry) {
      videoOpenRef.current = false;
      setVideoOpen(false);
      setVideoState("idle");
      setVideoErrorMessage("");
      setVideoRetryVersion(0);

      return clearTimer;
    }

    // When the SAME field/tab is refreshed or re-triggered while its
    // video is loading/playing, keep the preview open.
    if (
      sameLogicalEntry &&
      videoOpenRef.current
    ) {
      return clearTimer;
    }

    // A different field/tab still replaces the current preview normally.
    videoOpenRef.current = false;
    setVideoOpen(false);
    setVideoState("idle");
    setVideoErrorMessage("");
    setVideoRetryVersion(0);

    startTimer();

    return clearTimer;
  }, [
    entry?.code_label,
    entry?.field_label,
    messageVersion,
    clearTimer,
    startTimer,
  ]);

  useEffect(() => {
    const DEFAULT_RIGHT = 28;
    const TOOLBAR_GAP = 18;
    const TOOLBAR_HOST_SELECTOR =
      '[data-edge-mentor-toolbar-host="modeling"]';
    const TOOLBAR_EXPANDED_SELECTOR =
      '[data-edge-mentor-toolbar="modeling"]';

    const isToolbarPanelVisible = (toolbarPanel) => {
      if (!toolbarPanel?.isConnected) {
        return false;
      }

      if (
        toolbarPanel.closest(
          '[hidden], [aria-hidden="true"], [inert]',
        )
      ) {
        return false;
      }

      let currentElement = toolbarPanel;

      while (
        currentElement &&
        currentElement !== document.documentElement
      ) {
        const currentStyle =
          window.getComputedStyle(currentElement);

        if (
          currentStyle.display === "none" ||
          currentStyle.visibility === "hidden" ||
          currentStyle.visibility === "collapse" ||
          Number(currentStyle.opacity) === 0
        ) {
          return false;
        }

        currentElement = currentElement.parentElement;
      }

      const toolbarRect =
        toolbarPanel.getBoundingClientRect();

      if (
        toolbarRect.width <= 0 ||
        toolbarRect.height <= 0
      ) {
        return false;
      }

      // Start with the part of the toolbar that intersects the viewport.
      // A mounted toolbar can still have a valid DOM rectangle while being
      // clipped by a parent layout (for example, on the Nozzles tab).
      let visibleLeft = Math.max(toolbarRect.left, 0);
      let visibleTop = Math.max(toolbarRect.top, 0);
      let visibleRight = Math.min(
        toolbarRect.right,
        window.innerWidth,
      );
      let visibleBottom = Math.min(
        toolbarRect.bottom,
        window.innerHeight,
      );

      if (
        visibleRight <= visibleLeft ||
        visibleBottom <= visibleTop
      ) {
        return false;
      }

      // Account for clipping/scroll containers in the ancestor chain.
      // getBoundingClientRect() alone does not tell us whether the element is
      // actually painted inside those clipping rectangles.
      currentElement = toolbarPanel.parentElement;

      while (
        currentElement &&
        currentElement !== document.documentElement
      ) {
        const currentStyle =
          window.getComputedStyle(currentElement);

        const clipsX = [
          "hidden",
          "clip",
          "auto",
          "scroll",
        ].includes(currentStyle.overflowX);

        const clipsY = [
          "hidden",
          "clip",
          "auto",
          "scroll",
        ].includes(currentStyle.overflowY);

        if (clipsX || clipsY) {
          const ancestorRect =
            currentElement.getBoundingClientRect();

          if (clipsX) {
            visibleLeft = Math.max(
              visibleLeft,
              ancestorRect.left,
            );
            visibleRight = Math.min(
              visibleRight,
              ancestorRect.right,
            );
          }

          if (clipsY) {
            visibleTop = Math.max(
              visibleTop,
              ancestorRect.top,
            );
            visibleBottom = Math.min(
              visibleBottom,
              ancestorRect.bottom,
            );
          }

          if (
            visibleRight <= visibleLeft ||
            visibleBottom <= visibleTop
          ) {
            return false;
          }
        }

        currentElement = currentElement.parentElement;
      }

      return true;
    };

    const getVisibleToolbarPanel = (selector) =>
      [...document.querySelectorAll(selector)].find(
        isToolbarPanelVisible,
      ) || null;

    const getToolbarPanel = () =>
      getVisibleToolbarPanel(
        TOOLBAR_HOST_SELECTOR,
      ) ||
      getVisibleToolbarPanel(
        TOOLBAR_EXPANDED_SELECTOR,
      );

    const updateMessagePosition = () => {
      if (
        typeof window === "undefined" ||
        typeof document === "undefined"
      ) {
        setMessageRightOffset(DEFAULT_RIGHT);
        return;
      }

      // Track the actual Modeling Toolbar width during its 0.2s transition.
      // The host marker stays mounted in both expanded and collapsed states,
      // while the existing expanded marker remains unchanged for compatibility.
      const toolbarPanel = getToolbarPanel();

      if (!toolbarPanel) {
        setMessageRightOffset(DEFAULT_RIGHT);
        return;
      }

      const toolbarRect =
        toolbarPanel.getBoundingClientRect();

      const expandedWidth = Number(
        toolbarPanel.getAttribute(
          "data-edge-mentor-toolbar-expanded-width",
        ),
      );

      const collapsedWidth = Number(
        toolbarPanel.getAttribute(
          "data-edge-mentor-toolbar-collapsed-width",
        ),
      );

      const hasTransitionWidths =
        Number.isFinite(expandedWidth) &&
        Number.isFinite(collapsedWidth) &&
        expandedWidth > collapsedWidth;

      if (!hasTransitionWidths) {
        // Backward-compatible fallback for a toolbar that only exposes the
        // original expanded marker.
        const expandedToolbar =
          getVisibleToolbarPanel(
            TOOLBAR_EXPANDED_SELECTOR,
          );

        if (!expandedToolbar) {
          setMessageRightOffset(DEFAULT_RIGHT);
          return;
        }

        const toolbarOffset =
          Math.ceil(
            window.innerWidth -
              toolbarRect.left +
              TOOLBAR_GAP,
          );

        setMessageRightOffset(
          Math.max(
            DEFAULT_RIGHT,
            toolbarOffset,
          ),
        );
        return;
      }

      const widthProgress = Math.max(
        0,
        Math.min(
          1,
          (toolbarRect.width - collapsedWidth) /
            (expandedWidth - collapsedWidth),
        ),
      );

      const toolbarRightMargin = Math.max(
        0,
        window.innerWidth - toolbarRect.right,
      );

      // At 0% width progress the message stays at its normal 28px offset.
      // At 100% it lands at the exact same expanded-toolbar position used
      // before this smooth-motion refinement.
      const expandedRightOffset = Math.max(
        DEFAULT_RIGHT,
        toolbarRightMargin +
          expandedWidth +
          TOOLBAR_GAP,
      );

      const nextRightOffset = Math.ceil(
        DEFAULT_RIGHT +
          widthProgress *
            (expandedRightOffset - DEFAULT_RIGHT),
      );

      setMessageRightOffset(nextRightOffset);
    };

    let positionFrame = null;

    const schedulePositionUpdate = () => {
      if (positionFrame) {
        return;
      }

      positionFrame =
        window.requestAnimationFrame(() => {
          positionFrame = null;
          updateMessagePosition();
        });
    };

    let observedToolbarPanel = null;

    const toolbarResizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            schedulePositionUpdate();
          })
        : null;

    const syncToolbarObserver = () => {
      const toolbarPanel = getToolbarPanel();

      if (toolbarPanel === observedToolbarPanel) {
        schedulePositionUpdate();
        return;
      }

      if (observedToolbarPanel) {
        toolbarResizeObserver?.unobserve(
          observedToolbarPanel,
        );
      }

      observedToolbarPanel = toolbarPanel;

      if (observedToolbarPanel) {
        toolbarResizeObserver?.observe(
          observedToolbarPanel,
        );
      }

      schedulePositionUpdate();
    };

    const firstFrame =
      window.requestAnimationFrame(
        syncToolbarObserver,
      );

    const settleTimer =
      window.setTimeout(
        syncToolbarObserver,
        120,
      );

    window.addEventListener(
      "resize",
      schedulePositionUpdate,
    );

    // Reconnect the ResizeObserver if the toolbar is mounted/unmounted by a
    // tab change. Width changes during expand/collapse are handled directly
    // by ResizeObserver, so there is no polling and no delayed jump.
    const toolbarMutationObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver((mutations) => {
            const toolbarChanged =
              mutations.some((mutation) => {
                if (mutation.type === "attributes") {
                  return true;
                }

                if (mutation.type !== "childList") {
                  return false;
                }

                return [
                  ...mutation.addedNodes,
                  ...mutation.removedNodes,
                ].some((node) =>
                  Boolean(
                    node?.nodeType === 1 &&
                      (node.matches?.(
                        TOOLBAR_HOST_SELECTOR,
                      ) ||
                        node.matches?.(
                          TOOLBAR_EXPANDED_SELECTOR,
                        ) ||
                        node.querySelector?.(
                          TOOLBAR_HOST_SELECTOR,
                        ) ||
                        node.querySelector?.(
                          TOOLBAR_EXPANDED_SELECTOR,
                        )),
                  ),
                );
              });

            if (toolbarChanged) {
              syncToolbarObserver();
            }
          })
        : null;

    toolbarMutationObserver?.observe(
      document.body,
      {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: [
          "data-edge-mentor-toolbar",
          "data-edge-mentor-toolbar-host",
        ],
      },
    );

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.clearTimeout(settleTimer);

      if (positionFrame) {
        window.cancelAnimationFrame(
          positionFrame,
        );
      }

      if (observedToolbarPanel) {
        toolbarResizeObserver?.unobserve(
          observedToolbarPanel,
        );
      }

      toolbarResizeObserver?.disconnect();
      toolbarMutationObserver?.disconnect();

      window.removeEventListener(
        "resize",
        schedulePositionUpdate,
      );
    };
  }, [entry, messageVersion]);

  const handleMouseEnter =
    useCallback(() => {
      if (!entry) {
        return;
      }

      hoveredRef.current = true;
      pauseTimer();
    }, [entry, pauseTimer]);

  const handleMouseLeave =
    useCallback(() => {
      if (!entry) {
        return;
      }

      hoveredRef.current = false;

      if (
        focusedRef.current ||
        videoOpenRef.current
      ) {
        return;
      }

      if (remainingMsRef.current <= 0) {
        onClose?.();
        return;
      }

      startTimer();
    }, [entry, onClose, startTimer]);

  const handleFocusCapture =
    useCallback(() => {
      if (!entry) {
        return;
      }

      focusedRef.current = true;
      pauseTimer();
    }, [entry, pauseTimer]);

  const handleBlurCapture =
    useCallback(
      (event) => {
        if (!entry) {
          return;
        }

        const nextFocusedElement =
          event.relatedTarget;

        if (
          nextFocusedElement &&
          event.currentTarget.contains(
            nextFocusedElement,
          )
        ) {
          return;
        }

        focusedRef.current = false;

        if (
          hoveredRef.current ||
          videoOpenRef.current
        ) {
          return;
        }

        if (
          remainingMsRef.current <= 0
        ) {
          onClose?.();
          return;
        }

        startTimer();
      },
      [entry, onClose, startTimer],
    );

  if (!entry) {
    return null;
  }

  const isDark =
    theme.palette.mode === "dark";

  const isTabMessage =
    String(entry.message_type || "")
      .trim()
      .toLowerCase() === "tab";

  const {
    accent,
    Icon: MessageIcon,
    symbolKind: messageSymbolKind,
  } = getMessageVisual(
    entry,
    theme,
    isTabMessage,
  );

  const title =
    entry.field_label ||
    entry.code_label ||
    "Edge Mentor";

  const sectionTitle = String(
    entry.section_title || "",
  ).trim();

  const message =
    entry.sample_tooltip ||
    entry.how_used ||
    "";

  const uidLabel = uidTypeLabel(
    entry.uid_type,
  );

  const videoResolution =
    resolveEdgeMentorVideoSource(entry);

  const videoSource =
    videoResolution.src;

  const hasConfiguredVideo =
    Boolean(videoResolution.configured);

  const fieldValidationVideoTitle = String(
    entry.validation_video_title || "",
  ).trim();

  const hasFieldValidationVideo =
    !isTabMessage &&
    Boolean(
      entry.has_validation_video &&
        fieldValidationVideoTitle,
    );

  const fieldValidationVideoReady =
    hasFieldValidationVideo &&
    entry.validation_video_state === "ready" &&
    hasConfiguredVideo;

  const tabVideoName = String(
    entry.video_name || "",
  ).trim();

  const hasTabVideo =
    isTabMessage &&
    Boolean(
      entry.has_tab_video &&
        tabVideoName,
    );

  const tabVideoReady =
    hasTabVideo &&
    entry.tab_video_state === "ready" &&
    hasConfiguredVideo;

  const cardBackground = isDark
    ? alpha(
        theme.palette.background.paper,
        0.992,
      )
    : theme.palette.background.paper;

  const subtleSurface = alpha(accent, 0.075);
  const subtleBorder = alpha(accent, 0.48);
  const normalBorder = alpha(
    theme.palette.text.primary,
    isDark ? 0.18 : 0.14,
  );

  // Refined Edge Mentor shell styling from the approved layout guide.
  // Message content and video-button logic remain unchanged.
  const shellAccent = isTabMessage
    ? "#A56BFF"
    : "#4A8BFF";

  const shellSurface = isDark
    ? "#151C2A"
    : theme.palette.background.paper;

  const shellHeaderSurface = isDark
    ? "#182131"
    : alpha(theme.palette.primary.main, 0.025);

  const openVideoDialog = () => {
    videoOpenRef.current = true;
    pauseTimer();
    setVideoOpen(true);
  };

  const handleOpenVideo = () => {
    if (!hasConfiguredVideo) {
      return;
    }

    setVideoErrorMessage("");

    if (!videoResolution.playable) {
      edgeMentorDiagnostic(
        "video-source-unsupported",
        {
          uiLabel: title,
          codeLabel: entry.code_label,
          videoSource:
            videoResolution.rawSource,
          sourceType:
            videoResolution.sourceType,
        },
      );

      setVideoState("error");
      setVideoErrorMessage(
        "We couldn’t load this guide right now. Please try again, or contact support if the issue continues.",
      );

      openVideoDialog();
      return;
    }

    setVideoState("loading");
    openVideoDialog();
  };

  const handleCloseVideo = () => {
    videoOpenRef.current = false;
    setVideoOpen(false);
    setVideoState("idle");
    setVideoErrorMessage("");

    if (
      !hoveredRef.current &&
      !focusedRef.current &&
      remainingMsRef.current > 0
    ) {
      startTimer();
    }
  };

  const handleVideoLoadStart = () => {
    setVideoState("loading");
    setVideoErrorMessage("");
  };

  const handleVideoReady = () => {
    setVideoState("ready");
    setVideoErrorMessage("");
  };

  const handleVideoError = (event) => {
    const mediaError =
      event?.currentTarget?.error;

    const mediaErrorCode =
      Number(mediaError?.code) || 0;

    const mediaErrorLabels = {
      1: "MEDIA_ERR_ABORTED",
      2: "MEDIA_ERR_NETWORK",
      3: "MEDIA_ERR_DECODE",
      4: "MEDIA_ERR_SRC_NOT_SUPPORTED",
    };

    const mediaErrorText =
      mediaErrorLabels[mediaErrorCode] ||
      mediaError?.message ||
      "Unknown media loading error";

    edgeMentorDiagnostic(
      "video-load-failed",
      {
        uiLabel: title,
        codeLabel: entry.code_label,
        videoSource:
          videoResolution.rawSource,
        sourceType:
          videoResolution.sourceType,
        mediaError: mediaErrorText,
      },
    );

    setVideoState("error");
    setVideoErrorMessage(
      "We couldn’t load this guide right now. Please try again, or contact support if the issue continues.",
    );
  };

  const handleRetryVideo = () => {
    if (!videoResolution.playable) {
      return;
    }

    setVideoErrorMessage("");
    setVideoState("loading");

    setVideoRetryVersion(
      (value) => value + 1,
    );
  };

  return (
    <>
      <Paper
        elevation={0}
        role="dialog"
        aria-live="polite"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocusCapture={handleFocusCapture}
        onBlurCapture={handleBlurCapture}
        sx={{
          position: "fixed",
          right: messageRightOffset,
          bottom: 28,
          width: "min(360px, calc(100vw - 32px))",
          maxHeight: "50vh",
          zIndex: 9999,
          overflow: "hidden",
          borderRadius: "14px",
          border: `1px solid ${
            isDark
              ? "rgba(255,255,255,0.10)"
              : normalBorder
          }`,
          backgroundColor: shellSurface,
          color: theme.palette.text.primary,
          boxShadow: isDark
            ? "0 20px 50px rgba(0,0,0,0.42), 0 0 0 1px rgba(255,255,255,0.018)"
            : "0 18px 42px rgba(15,23,42,0.16)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -42,
            left: -30,
            width: 150,
            height: 150,
            pointerEvents: "none",
            background: `radial-gradient(circle, ${alpha(
              shellAccent,
              isDark ? 0.13 : 0.08,
            )} 0%, transparent 68%)`,
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            maxHeight: "inherit",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                "40px minmax(0, 1fr) 28px",
              alignItems: "center",
              columnGap: "12px",
              px: "18px",
              py: "15px",
              backgroundColor: shellHeaderSurface,
              borderBottom: `1px solid ${
                isDark
                  ? "rgba(255,255,255,0.075)"
                  : alpha(theme.palette.divider, 0.65)
              }`,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: shellAccent,
                backgroundColor: alpha(
                  shellAccent,
                  isDark ? 0.11 : 0.075,
                ),
                border: `1px solid ${alpha(
                  shellAccent,
                  isDark ? 0.42 : 0.30,
                )}`,
                boxShadow: "none",
              }}
            >
              {isTabMessage ? (
                <SettingsOutlinedIcon
                  sx={{ fontSize: 25 }}
                />
              ) : (
                <InfoOutlinedIcon
                  sx={{ fontSize: 25 }}
                />
              )}
            </Box>

            <Typography
              sx={{
                ...fullTextSx,
                minWidth: 0,
                fontSize: "15.5px",
                fontWeight: 650,
                lineHeight: 1.25,
                color: isDark
                  ? "#E6EDF7"
                  : theme.palette.text.primary,
                letterSpacing: "-0.008em",
              }}
            >
              {title}
            </Typography>

            <IconButton
              size="small"
              aria-label="Close Edge Mentor message"
              onClick={onClose}
              sx={{
                width: 28,
                height: 28,
                p: 0,
                borderRadius: "50%",
                color: isDark
                  ? "#8F9AAC"
                  : theme.palette.text.secondary,
                "&:hover": {
                  color: theme.palette.text.primary,
                  backgroundColor: alpha(
                    theme.palette.text.primary,
                    0.055,
                  ),
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              px: "18px",
              pt: "15px",
              pb: "17px",
              display: "flex",
              flexDirection: "column",
              flex: "1 1 auto",
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                flex: "1 1 auto",
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
            {isTabMessage ? (
              <Typography
                component="div"
                sx={{
                  ...fullTextSx,
                  fontSize: "13px",
                  lineHeight: 1.55,
                  fontWeight: 400,
                  color: isDark
                    ? "#C7D1E0"
                    : theme.palette.text.secondary,
                }}
              >
                {sectionTitle && (
                  <>
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 600,
                        color: isDark
                          ? "#E6EDF7"
                          : theme.palette.text.primary,
                      }}
                    >
                      {sectionTitle}:
                    </Box>{" "}
                  </>
                )}
                {message}
              </Typography>
            ) : (
              <>
                {uidLabel && (
                  <Typography
                    component="div"
                    sx={{
                      ...fullTextSx,
                      mb: "11px",
                      fontSize: "13px",
                      lineHeight: 1.55,
                      fontWeight: 400,
                      color: isDark
                        ? "#C7D1E0"
                        : theme.palette.text.secondary,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 600,
                        color: isDark
                          ? "#E6EDF7"
                          : theme.palette.text.primary,
                      }}
                    >
                      {uidLabel}:
                    </Box>{" "}
                    {message}
                  </Typography>
                )}

                {entry.where_shown && (
                  <Typography
                    component="div"
                    sx={{
                      ...fullTextSx,
                      fontSize: "13px",
                      lineHeight: 1.55,
                      color: isDark
                        ? "#8FA1B8"
                        : theme.palette.text.secondary,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontWeight: 600 }}
                    >
                      Shown in:
                    </Box>{" "}
                    {entry.where_shown}
                  </Typography>
                )}
              </>
            )}
            </Box>

            {(hasTabVideo || hasFieldValidationVideo) && (
              <Tooltip
                title={
                  isTabMessage
                    ? tabVideoReady
                      ? `Watch ${tabVideoName || title}`
                      : "Coming soon"
                    : fieldValidationVideoReady
                      ? fieldValidationVideoTitle
                      : "Coming soon"
                }
              >
                <span
                  style={{
                    display: "block",
                    width: "100%",
                  }}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={
                      isTabMessage
                        ? !tabVideoReady
                        : !fieldValidationVideoReady
                    }
                    onClick={handleOpenVideo}
                    startIcon={
                      <PlayCircleOutlineRoundedIcon />
                    }
                    sx={{
                      mt: "16px",
                      minHeight: 42,
                      px: 1.25,
                      justifyContent: "flex-start",
                      borderRadius: "9px",
                      borderColor: isDark
                        ? "rgba(255,255,255,0.14)"
                        : normalBorder,
                      backgroundColor: isDark
                        ? "rgba(255,255,255,0.035)"
                        : alpha(
                            theme.palette.text.primary,
                            0.022,
                          ),
                      color: isDark
                        ? "#DCE5F2"
                        : theme.palette.text.primary,
                      fontSize: "12.5px",
                      fontWeight: 500,
                      lineHeight: 1.35,
                      textTransform: "none",
                      textAlign: "left",
                      "& .MuiButton-startIcon": {
                        mr: 1,
                        ml: 0,
                        width: 26,
                        height: 26,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                      "& .MuiButton-startIcon > *:nth-of-type(1)": {
                        width: 26,
                        height: 26,
                        fontSize: "26px !important",
                      },
                      "&:hover": {
                        borderColor: alpha(
                          shellAccent,
                          0.58,
                        ),
                        backgroundColor: isDark
                          ? alpha(
                              shellAccent,
                              0.06,
                            )
                          : alpha(
                              shellAccent,
                              0.055,
                            ),
                      },
                      "&.Mui-disabled": {
                        borderColor: isDark
                          ? "rgba(255,255,255,0.10)"
                          : normalBorder,
                        backgroundColor: isDark
                          ? "rgba(255,255,255,0.025)"
                          : alpha(
                              theme.palette.text.primary,
                              0.018,
                            ),
                        color: isDark
                          ? "rgba(199,209,224,0.48)"
                          : theme.palette.text.disabled,
                      },
                    }}
                  >
                    {isTabMessage
                      ? tabVideoReady
                        ? `Watch ${tabVideoName}`
                        : `${tabVideoName} Coming Soon`
                      : fieldValidationVideoReady
                        ? fieldValidationVideoTitle
                        : `${fieldValidationVideoTitle} (Coming Soon)`}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={videoOpen}
        onClose={handleCloseVideo}
        maxWidth={false}
        fullWidth
        sx={{ zIndex: 10050 }}
        PaperProps={{
          sx: {
            width:
              "min(1240px, calc(100vw - 56px))",
            maxWidth: "none",
            maxHeight:
              "calc(100vh - 48px)",
            overflow: "hidden",
            borderRadius: 2.3,
            border: `1px solid ${alpha(
              accent,
              0.38,
            )}`,
            backgroundColor:
              theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle
          sx={{
            minHeight: 76,
            display: "flex",
            alignItems: "center",
            justifyContent:
              "space-between",
            gap: 1.5,
            py: 1.55,
            px: 2,
            pr: 1.35,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: accent,
                border: `1px solid ${alpha(
                  accent,
                  0.34,
                )}`,
                backgroundColor: alpha(
                  accent,
                  0.08,
                ),
              }}
            >
              <PlayCircleOutlineRoundedIcon
                sx={{ fontSize: 30 }}
              />
            </Box>

            <Box
              sx={{ minWidth: 0 }}
            >
              <Typography
                component="div"
                sx={{
                  ...fullTextSx,
                  fontSize: "1rem",
                  fontWeight: 760,
                  lineHeight: 1.3,
                }}
              >
                {isTabMessage
                  ? tabVideoName || title
                  : title}
              </Typography>

              <Typography
                component="div"
                sx={{
                  ...fullTextSx,
                  mt: 0.3,
                  fontSize: "0.8rem",
                  lineHeight: 1.4,
                  color:
                    theme.palette.text
                      .secondary,
                }}
              >
                Video guide
              </Typography>
            </Box>
          </Box>

          <IconButton
            size="small"
            aria-label="Close video preview"
            onClick={handleCloseVideo}
            sx={{
              width: 34,
              height: 34,
            }}
          >
            <CloseIcon
              sx={{ fontSize: 21 }}
            />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            position: "relative",
            p: 0,
            backgroundColor: "#000",
            minHeight: "560px",
          }}
        >
          {videoResolution.playable &&
            videoState !== "error" && (
              <video
                key={`${videoSource}-${videoRetryVersion}`}
                src={videoSource}
                controls
                autoPlay
                playsInline
                preload="metadata"
                onLoadStart={
                  handleVideoLoadStart
                }
                onLoadedMetadata={
                  handleVideoReady
                }
                onLoadedData={
                  handleVideoReady
                }
                onCanPlay={
                  handleVideoReady
                }
                onPlaying={
                  handleVideoReady
                }
                onError={
                  handleVideoError
                }
                style={{
                  display: "block",
                  width: "100%",
                  height:
                    "calc(100vh - 160px)",
                  minHeight: "560px",
                  maxHeight: "820px",
                  objectFit: "contain",
                  backgroundColor: "#000",
                }}
              >
                Your browser does not support
                video playback.
              </video>
            )}

          {videoState === "loading" && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                backgroundColor:
                  "rgba(0,0,0,0.2)",
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "0.9rem",
                  fontWeight: 650,
                }}
              >
                Loading video guide...
              </Typography>
            </Box>
          )}

          {videoState === "error" && (
            <Box
              role="alert"
              sx={{
                minHeight: "560px",
                height:
                  "calc(100vh - 160px)",
                maxHeight: "820px",
                px: 3,
                py: 5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                backgroundColor: "#000",
                color: "#fff",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: 68,
                  height: 58,
                  mb: 1.8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <OndemandVideoOutlinedIcon
                  sx={{
                    fontSize: 56,
                    color:
                      "rgba(255,255,255,0.92)",
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    right: -1,
                    bottom: -1,
                    width: 29,
                    height: 29,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor:
                      theme.palette.warning.main,
                    boxShadow:
                      "0 0 0 3px #000",
                  }}
                >
                  <PriorityHighRoundedIcon
                    sx={{
                      fontSize: 19,
                      color:
                        theme.palette.warning
                          .contrastText,
                    }}
                  />
                </Box>
              </Box>

              <Typography
                sx={{
                  maxWidth: 560,
                  fontSize: "1rem",
                  fontWeight: 760,
                  lineHeight: 1.45,
                }}
              >
                Video unavailable
              </Typography>

              <Typography
                sx={{
                  maxWidth: 620,
                  mt: 0.85,
                  fontSize: "0.84rem",
                  lineHeight: 1.55,
                  color:
                    "rgba(255,255,255,0.72)",
                }}
              >
                {videoErrorMessage ||
                  "We couldn’t load this guide right now. Please try again, or contact support if the issue continues."}
              </Typography>

              {videoResolution.playable && (
                <Button
                  variant="outlined"
                  startIcon={
                    <ReplayRoundedIcon />
                  }
                  onClick={
                    handleRetryVideo
                  }
                  sx={{
                    mt: 2.2,
                    px: 2.2,
                    color:
                      theme.palette.warning
                        .contrastText,
                    borderColor:
                      theme.palette.warning.main,
                    backgroundColor:
                      theme.palette.warning.main,
                    textTransform: "none",
                    fontWeight: 720,
                    "&:hover": {
                      borderColor:
                        theme.palette.warning.dark,
                      backgroundColor:
                        theme.palette.warning.dark,
                    },
                  }}
                >
                  Try Again
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
